(() => {
  'use strict';
  const canvas = document.getElementById('gpc-atmosphere');
  if (!canvas) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = matchMedia('(pointer: coarse)');
  const ambientSpeed = () => coarse.matches ? 1.2 : 1;
  let gl, frame = 0, lost = false, time = 0, last = 0, accumulator = 0;
  let width = 0, height = 0, fields = [], programs = [], buffer;
  let velocity, flow, pressure, divergence, curl, picture;
  let pendingPointer = null, lastPointer = null, scrollImpulse = 0, lastScroll = scrollY;
  const paused = () => reduced.matches || document.documentElement.classList.contains('gpc-motion-off');
  const vertex = `#version 300 es
    in vec2 aPosition; out vec2 uv;
    void main(){ uv=aPosition*.5+.5; gl_Position=vec4(aPosition,0.,1.); }`;
  const common = `#version 300 es
    precision highp float; precision highp sampler2D;
    in vec2 uv; out vec4 result;
    uniform vec2 pixel; uniform float dt; uniform float time; uniform float aspect;
    uniform sampler2D a; uniform sampler2D b;
    float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
    float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);}
    float fbm(vec2 p){float v=0.,w=.5;for(int i=0;i<4;i++){v+=w*noise(p);p=mat2(1.6,1.2,-1.2,1.6)*p+8.3;w*=.5;}return v;}
  `;
  const shaders = {
    advect: `uniform float decay; void main(){vec2 v=texture(a,uv).xy; vec2 back=clamp(uv-v*dt, pixel*.5,1.-pixel*.5);result=texture(b,back)*exp(-decay*dt);}`,
    force: `uniform vec4 pointer; uniform vec2 impulse; uniform float scrollForce;
      void main(){vec2 v=texture(a,uv).xy;vec2 p=vec2(uv.x*aspect,uv.y);
      float n=fbm(p*3.5+vec2(time*.035,-time*.024));
      v+=vec2(sin(uv.y*9.+time*.22+n*4.)*.012, .007+cos(uv.x*9.+time*.19+n*4.)*.004)*dt;
      v.y+=scrollForce*dt;
      vec2 d=(uv-pointer.xy)*vec2(aspect,1.);float radius=.032;
      float influence=exp(-dot(d,d)/radius)*pointer.z;
      v+=vec2(impulse.x/aspect,impulse.y)*influence;
      v+=vec2(-d.y/aspect,d.x)*influence*pointer.w*.16;
      if(uv.x<pixel.x||uv.x>1.-pixel.x)v.x=0.;if(uv.y<pixel.y||uv.y>1.-pixel.y)v.y=0.;
      result=vec4(clamp(v,vec2(-.65),vec2(.65)),0.,1.);}`,
    curl: `void main(){float l=texture(a,uv-vec2(pixel.x,0)).y,r=texture(a,uv+vec2(pixel.x,0)).y,t=texture(a,uv+vec2(0,pixel.y)).x,bottom=texture(a,uv-vec2(0,pixel.y)).x;result=vec4((r-l-t+bottom)*.5,0,0,1);}`,
    vorticity: `void main(){float l=abs(texture(b,uv-vec2(pixel.x,0)).x),r=abs(texture(b,uv+vec2(pixel.x,0)).x),t=abs(texture(b,uv+vec2(0,pixel.y)).x),bottom=abs(texture(b,uv-vec2(0,pixel.y)).x),c=texture(b,uv).x;vec2 f=.5*vec2(t-bottom,r-l);f/=length(f)+.00001;f*=c*12.;f.y*=-1.;result=vec4(texture(a,uv).xy+f*dt,0,1);}`,
    divergence: `void main(){vec2 l=texture(a,uv-vec2(pixel.x,0)).xy,r=texture(a,uv+vec2(pixel.x,0)).xy,t=texture(a,uv+vec2(0,pixel.y)).xy,bottom=texture(a,uv-vec2(0,pixel.y)).xy;result=vec4(.5*(r.x-l.x+t.y-bottom.y),0,0,1);}`,
    pressure: `void main(){float l=texture(a,uv-vec2(pixel.x,0)).x,r=texture(a,uv+vec2(pixel.x,0)).x,t=texture(a,uv+vec2(0,pixel.y)).x,bottom=texture(a,uv-vec2(0,pixel.y)).x;result=vec4((l+r+t+bottom-texture(b,uv).x)*.25,0,0,1);}`,
    project: `void main(){float l=texture(b,uv-vec2(pixel.x,0)).x,r=texture(b,uv+vec2(pixel.x,0)).x,t=texture(b,uv+vec2(0,pixel.y)).x,bottom=texture(b,uv-vec2(0,pixel.y)).x;result=vec4(texture(a,uv).xy-vec2(r-l,t-bottom)*.5,0,1);}`,
    flow: `void main(){vec2 v=texture(b,uv).xy;vec2 old=texture(a,clamp(uv-v*dt,pixel,1.-pixel)).xy;result=vec4(clamp(old*exp(-.32*dt)+v*dt,vec2(-.13),vec2(.13)),0,1);}`,
    display: `uniform sampler2D photo; uniform float strength; uniform float photoAspect;
      void main(){
        vec2 drift=texture(a,uv).xy*.5;
        drift+=(texture(a,uv+vec2(pixel.x,0)).xy+texture(a,uv-vec2(pixel.x,0)).xy+texture(a,uv+vec2(0,pixel.y)).xy+texture(a,uv-vec2(0,pixel.y)).xy)*.125;
        vec2 q=uv-drift*.38;
        q+=vec2(sin(time*.09+uv.y*3.),cos(time*.075+uv.x*2.))*.012;
        q+=vec2(fbm(uv*4.+time*.009),fbm(uv*4.+8.-time*.007))*.007;
        vec2 fit=aspect>photoAspect?vec2(1.,photoAspect/aspect):vec2(aspect/photoAspect,1.);
        q=(q-.5)*fit+.5;
        vec3 sampleColor=texture(photo,clamp(q,vec2(.001),vec2(.999))).rgb;
        float luminance=dot(sampleColor,vec3(.2126,.7152,.0722));
        float alpha=clamp(pow(luminance,1.12)*strength,0.,strength);
        // The browser compositor expects premultiplied colors. Unassociated
        // pale RGB can become a full-screen gray layer on iOS (WebKit 264081).
        result=vec4(vec3(.82,.86,.88)*alpha,alpha);
      }`
  };
  const compile = (type,source) => {
    const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);return shader;
  };
  function program(source) {
    const vs=compile(gl.VERTEX_SHADER,vertex),fs=compile(gl.FRAGMENT_SHADER,common+source);
    const handle=gl.createProgram();gl.attachShader(handle,vs);gl.attachShader(handle,fs);
    gl.bindAttribLocation(handle,0,'aPosition');gl.linkProgram(handle);
    gl.deleteShader(vs);gl.deleteShader(fs);
    if(!gl.getProgramParameter(handle,gl.LINK_STATUS)){const message=gl.getProgramInfoLog(handle);gl.deleteProgram(handle);throw Error(message);}
    const uniforms={};for(let i=0;i<gl.getProgramParameter(handle,gl.ACTIVE_UNIFORMS);i++){const u=gl.getActiveUniform(handle,i);uniforms[u.name]=gl.getUniformLocation(handle,u.name);}
    const p={handle,uniforms};programs.push(p);return p;
  }
  function target() {
    const texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA16F,width,height,0,gl.RGBA,gl.HALF_FLOAT,null);
    const framebuffer=gl.createFramebuffer();gl.bindFramebuffer(gl.FRAMEBUFFER,framebuffer);gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,texture,0);
    if(gl.checkFramebufferStatus(gl.FRAMEBUFFER)!==gl.FRAMEBUFFER_COMPLETE){gl.deleteTexture(texture);gl.deleteFramebuffer(framebuffer);throw Error('Unsupported smoke framebuffer');}
    gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);
    const field={texture,framebuffer};fields.push(field);return field;
  }
  const double = () => ({read:target(),write:target(),swap(){[this.read,this.write]=[this.write,this.read];}});
  let passes;
  function draw(p,dest,inputs={},values={}) {
    gl.useProgram(p.handle);gl.bindFramebuffer(gl.FRAMEBUFFER,dest?.framebuffer||null);
    gl.viewport(0,0,dest?width:canvas.width,dest?height:canvas.height);
    const u=p.uniforms;gl.uniform2f(u.pixel,1/width,1/height);gl.uniform1f(u.dt,1/30);gl.uniform1f(u.time,time);gl.uniform1f(u.aspect,innerWidth/innerHeight);
    let unit=0;for(const [name,field] of Object.entries(inputs)){gl.activeTexture(gl.TEXTURE0+unit);gl.bindTexture(gl.TEXTURE_2D,field.texture);gl.uniform1i(u[name],unit++);}
    for(const [name,value] of Object.entries(values)){
      if(Array.isArray(value)){if(value.length===2)gl.uniform2f(u[name],...value);else gl.uniform4f(u[name],...value);}else gl.uniform1f(u[name],value);
    }
    gl.drawArrays(gl.TRIANGLES,0,3);
  }
  function resize() {
    if(!gl||lost)return;
    const ratio=innerWidth/innerHeight,short=coarse.matches?112:192;
    const nextW=Math.round(ratio>=1?short*ratio:short),nextH=Math.round(ratio>=1?short:short/ratio);
    const scale=Math.min(1,Math.sqrt((coarse.matches?550000:1800000)/(innerWidth*innerHeight)));
    canvas.width=Math.round(innerWidth*scale);canvas.height=Math.round(innerHeight*scale);
    if(nextW===width&&nextH===height)return;
    fields.forEach(f=>{gl.deleteTexture(f.texture);gl.deleteFramebuffer(f.framebuffer);});fields=[];width=nextW;height=nextH;
    velocity=double();flow=double();pressure=double();divergence=target();curl=target();
    render();
  }
  function render(){if(picture)draw(passes.display,null,{a:flow.read,photo:picture},{strength:coarse.matches?.37:.46,photoAspect:picture.aspect});}
  function step() {
    // Slightly quicker autonomous sway on touch screens. Keep the solver step,
    // distortion amplitude, opacity and texture resolution exactly as before.
    time+=ambientSpeed()/30;
    draw(passes.advect,velocity.write,{a:velocity.read,b:velocity.read},{decay:.35});velocity.swap();
    const p=pendingPointer;pendingPointer=null;
    draw(passes.force,velocity.write,{a:velocity.read},{pointer:p?[p.x,p.y,1,p.spin]:[0,0,0,0],impulse:p?[p.dx,p.dy]:[0,0],scrollForce:scrollImpulse});velocity.swap();scrollImpulse*=.82;
    draw(passes.curl,curl,{a:velocity.read});draw(passes.vorticity,velocity.write,{a:velocity.read,b:curl});velocity.swap();
    draw(passes.divergence,divergence,{a:velocity.read});
    for(let i=0;i<(coarse.matches?10:16);i++){draw(passes.pressure,pressure.write,{a:pressure.read,b:divergence});pressure.swap();}
    draw(passes.project,velocity.write,{a:velocity.read,b:pressure.read});velocity.swap();
    draw(passes.flow,flow.write,{a:flow.read,b:velocity.read});flow.swap();
  }
  function tick(now) {
    frame=0;if(lost||document.hidden||paused())return;
    accumulator+=Math.min((now-last)/1000,.06);last=now;
    if(accumulator>=1/30){step();render();accumulator%=1/30;}
    frame=requestAnimationFrame(tick);
  }
  function sync() {
    cancelAnimationFrame(frame);frame=0;
    if(!gl||lost)return;
    canvas.dataset.state=paused()?'paused':'running';
    canvas.dataset.ambientSpeed=String(ambientSpeed());
    if(!document.hidden&&!paused()){last=performance.now();accumulator=0;frame=requestAnimationFrame(tick);}
  }
  async function start() {
    try {
      gl=canvas.getContext('webgl2',{alpha:true,premultipliedAlpha:true,antialias:false,depth:false,stencil:false,powerPreference:'low-power'});
      if(!gl||!gl.getExtension('EXT_color_buffer_float'))throw Error('Smoke WebGL unavailable');
      programs=[];passes=Object.fromEntries(Object.entries(shaders).map(([name,source])=>[name,program(source)]));
      buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);gl.vertexAttribPointer(0,2,gl.FLOAT,false,0,0);gl.disable(gl.DEPTH_TEST);gl.disable(gl.BLEND);
      const photo=new Image();photo.crossOrigin='anonymous';
      await new Promise((resolve,reject)=>{photo.onload=resolve;photo.onerror=reject;photo.src=canvas.dataset.smokeSrc;});
      if(lost)return;
      const texture=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,texture);gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,photo);gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,false);
      picture={texture,aspect:photo.naturalWidth/photo.naturalHeight};
      width=0;height=0;lost=false;resize();canvas.dataset.engine='photographic-fluid';canvas.dataset.compositing='premultiplied';canvas.hidden=false;sync();
    } catch(error) {
      fields.forEach(f=>{gl?.deleteTexture(f.texture);gl?.deleteFramebuffer(f.framebuffer);});fields=[];
      programs.forEach(p=>gl?.deleteProgram(p.handle));programs=[];if(buffer)gl?.deleteBuffer(buffer);
      canvas.hidden=true;canvas.dataset.engine='unavailable';if(picture)gl?.deleteTexture(picture.texture);picture=null;
    }
  }
  window.addEventListener('pointermove',event=>{
    if(event.pointerType==='touch'||paused())return;
    const x=event.clientX/innerWidth,y=1-event.clientY/innerHeight,now=performance.now();
    if(lastPointer&&now-lastPointer.t<150){const dx=Math.max(-.09,Math.min(.09,(x-lastPointer.x)*3.5)),dy=Math.max(-.09,Math.min(.09,(y-lastPointer.y)*3.5));
      pendingPointer={x,y,dx,dy,spin:Math.sign(dx+dy)||1};canvas.dataset.pointerInput="received";}
    lastPointer={x,y,t:now};
  },{passive:true});
  window.addEventListener('scroll',()=>{scrollImpulse=Math.max(-.022,Math.min(.022,(scrollY-lastScroll)/Math.max(innerHeight,1)*.12));lastScroll=scrollY;},{passive:true});
  let resizeTimer;
  window.addEventListener('resize',()=>{clearTimeout(resizeTimer);resizeTimer=setTimeout(()=>{try{resize();}catch(_){canvas.hidden=true;}},150);},{passive:true});
  document.addEventListener('visibilitychange',sync);reduced.addEventListener('change',sync);
  new MutationObserver(sync).observe(document.documentElement,{attributes:true,attributeFilter:['class']});
  canvas.addEventListener('webglcontextlost',event=>{event.preventDefault();lost=true;cancelAnimationFrame(frame);canvas.hidden=true;});
  canvas.addEventListener('webglcontextrestored',()=>{lost=false;start();});
  start();
})();
